/**
 * The shape of every cosmetic's `data` blob and the closed lists of "kinds" the 3D builders (src/client/char3d) understand.
 * The catalogue (src/server/seed/cosmetics.ts) and the renderer share this file, so a typo in the catalogue is a type error – and a test
 * (tests/avatar.test.ts) checks that every catalogue entry names a kind that exists.
 */

export const SPECIES = ["kid", "blob", "fox", "dog", "cat", "bunny", "bear", "panda", "penguin", "owl", "dino", "dragon", "frog", "robot", "alien"] as const;
export type Species = (typeof SPECIES)[number];

export const HAIR_STYLES = ["spiky", "long", "bob", "pony", "buns", "afro", "buzz", "mohawk", "pigtails", "swoosh", "none"] as const;
export type HairStyle = (typeof HAIR_STYLES)[number];

export const OUTFIT_KINDS = [
  "tee", "hoodie", "jacket", "jersey", "dress", "overalls", "robe", "armor", "spacesuit", "gi", "suit", "coat",
  "pirate", "hero", "cyber", "kimono", "hawaii", "winter", "royal", "wizard",
] as const;
export type OutfitKind = (typeof OUTFIT_KINDS)[number];
export const EMBLEMS = ["star", "bolt", "brain", "heart", "number", "skull", "flame", "crest"] as const;
export type Emblem = (typeof EMBLEMS)[number];

export const HAT_KINDS = [
  "cap", "headband", "beanie", "chef", "antenna", "spikes", "bandana", "horns", "helmet", "wizard", "halo", "crown",
  "catears", "bunnyears", "tophat", "pirate", "cowboy", "party", "graduation", "flowers", "viking", "santa", "propeller", "detective",
  "kabuto", "knight", "astro", "jewelcrown", "flamecrown", "crystalcrown", "straw", "bearears", "mushroom", "pumpkin", "witch", "sprout", "dragonhelm", "laurel",
] as const;
export type HatKind = (typeof HAT_KINDS)[number];

export const FACE_KINDS = [
  "glasses", "round", "shades", "star", "monocle", "eyepatch", "moustache", "beard", "mask", "ninja", "headphones", "scarf", "visor",
  "freckles", "whiskers", "bandaid", "facepaint", "goggles",
] as const;
export type FaceKind = (typeof FACE_KINDS)[number];

export const BACK_KINDS = [
  "backpack", "cape", "wings_angel", "wings_bat", "wings_dragon", "wings_butterfly", "wings_crystal", "wings_gold", "jetpack", "balloons",
  "guitar", "quiver", "shell", "surfboard", "cloud", "lantern", "swordback", "rocket", "book", "bedroll",
] as const;
export type BackKind = (typeof BACK_KINDS)[number];

export const HAND_KINDS = [
  "pencil", "book", "wand", "sword", "torch", "trophy", "flag", "balloon", "lollipop", "football", "magnifier", "card", "telescope", "staff",
  "hammer", "mic", "icecream", "umbrella", "lightblade", "shield", "axe", "paintbrush", "globe", "bow",
] as const;
export type HandKind = (typeof HAND_KINDS)[number];

export const PET_KINDS = [
  "dragonling", "puppy", "kitten", "bunny", "owl", "foxkit", "drone", "slime", "ghost", "chick", "foal", "dino", "penguin", "cloud", "golem", "turtle", "bee", "jelly", "star",
] as const;
export type PetKind = (typeof PET_KINDS)[number];

export const AURA_KINDS = ["sparkle", "hearts", "leaves", "snow", "bubbles", "fire", "bolt", "stars", "code", "petals", "rainbow", "galaxy", "sun"] as const;
export type AuraKind = (typeof AURA_KINDS)[number];

export const EMOTE_KINDS = ["wave", "gg", "cheer", "confetti", "rocket", "bolt", "dance", "flex", "spin", "flip", "bow", "think", "laugh", "shrug", "salute", "point", "sit", "float"] as const;
export type EmoteKind = (typeof EMOTE_KINDS)[number];

export type SkinFx = "gold" | "glow" | "fire" | "ice" | "galaxy" | "rainbow" | "glitch";

export interface OutfitData { kind: OutfitKind; top: string; bottom: string; trim: string; shoes?: string; glow?: string; emblem?: Emblem }
export interface SkinData {
  species: Species;
  body: string;
  accent: string;
  /** belly / muzzle / inner-ear colour of the animals */
  belly?: string;
  hair?: HairStyle;
  hairColor?: string;
  eyeColor?: string;
  outfit?: OutfitData;
  fx?: SkinFx;
  /** overall scale of the figure (1 = normal) */
  size?: number;
}
export interface HatData { kind: HatKind; color: string; accent?: string }
export interface FaceData { kind: FaceKind; color: string; accent?: string }
export interface BackData { kind: BackKind; color: string; accent?: string }
export interface HandData { kind: HandKind; color: string; accent?: string }
export interface PetData { kind: PetKind; color: string; accent?: string }
export interface AuraData { kind: AuraKind; color: string; color2?: string }
export interface EmoteData { kind: EmoteKind; label: string; victory?: boolean }

/**
 * HEROES (v17). A hero is a complete, named character – body, clothes, hat, face, back, hand, pet, aura and victory emote designed
 * together – with a short story. It is ONE shop item with its own price, so a player can save up for "Nova" or "Drage-Dorte" instead of
 * for twenty loose pieces. Wearing a hero shows the whole look; picking any single piece afterwards takes the hero off again (the
 * player's own look is never lost). Heroes are cosmetic like everything else: no gameplay effect.
 */
export interface HeroParts {
  skin: SkinData;
  outfit?: OutfitData;
  accessory?: FaceData;
  hat?: HatData;
  back?: BackData;
  hand?: HandData;
  pet?: PetData;
  aura?: AuraData;
  emote?: EmoteData;
}
export interface HeroData {
  parts: HeroParts;
  /** one short line under the name, e.g. "Detektiven der aldrig giver op" */
  epithet: string;
  /** two or three sentences of story (Danish) */
  lore: string;
  /** the hero's two signature colours (card background, ring, silhouette glow) */
  tint: [string, string];
}
/** the slots a hero fills – the player's own pieces in these slots are covered while the hero is worn */
export const HERO_SLOTS = ["skin", "outfit", "accessory", "hat", "back", "hand", "pet", "aura", "emote"] as const;
export type HeroSlot = (typeof HERO_SLOTS)[number];
type LookItem = { id: string; data: Record<string, any>; name: string };
/** the loose pieces a hero is made of, as the equipped map (and the 3D figure) knows them – a slot the hero leaves empty is cleared */
export function heroLook(hero: { id: string; name: string; data: Record<string, any> }): Partial<Record<HeroSlot, LookItem>> {
  const parts = (hero.data?.parts ?? {}) as Record<string, Record<string, any> | undefined>;
  const out: Partial<Record<HeroSlot, LookItem>> = {};
  for (const slot of HERO_SLOTS) {
    const d = parts[slot];
    if (d) out[slot] = { id: `${hero.id}:${slot}`, data: d, name: hero.name };
  }
  return out;
}

/** The categories that make up the 3D figure (the rest – background, frame, banner … – are 2D). */
export const FIGURE_CATEGORIES = ["skin", "outfit", "hat", "accessory", "back", "hand", "pet", "aura"] as const;
export type FigureCategory = (typeof FIGURE_CATEGORIES)[number];

/** Items from this price and up are shown as "Mytisk" (the top prestige tier – technically still 'legendary' in the database). */
export const MYTHIC_FROM = 25000;
export type Tier = "common" | "rare" | "epic" | "legendary" | "mythic";
export const TIER_LABEL: Record<Tier, string> = { common: "Almindelig", rare: "Sjælden", epic: "Episk", legendary: "Legendarisk", mythic: "Mytisk" };
export const tierOf = (rarity: string, price: number): Tier => (rarity === "legendary" && price >= MYTHIC_FROM ? "mythic" : (rarity as Tier));

/** the default look of players who have not equipped anything and of the computer players */
export const DEFAULT_SKIN: SkinData = {
  species: "kid", body: "#f5c096", accent: "#1b2a6b", hair: "spiky", hairColor: "#6b3f22", eyeColor: "#4a2b17",
  outfit: { kind: "jacket", top: "#26357a", bottom: "#33415c", trim: "#ffd166", shoes: "#f1f5f9", emblem: "star" },
};
